import { useTranslation } from "react-i18next";
import { DOCUMENT_LOCALE, I18N_LOCALE_TO_DOCUMENT_LOCALE, IDocument, IRevision } from "../@types/document";
import DocumentCardThumbnail from "./DocumentCardThumbnail";
import { findLastValidPublishedRevision, getRevisionFileExtension, sortRevisionsByManualRevisionNumber } from "../lib/utils";
import { forwardRef, useContext } from "react";
import Icon from "./Icon";
import { getRevisionPublicUrl } from "../lib/api/api";
import { useModal } from "./Modal";
import RevisionHistory from "./RevisionHistory";
import DownloadButton from "./DownloadButton";
import { AppContext } from "../context/AppContextProvider";
import { ActionTypes } from "../actions/actions";

interface IDocCardProps {
    doc: IDocument;
}

export const DocCard = forwardRef<HTMLDivElement, IDocCardProps>(function DocCard({ doc }, ref) {
    const { state } = useContext(AppContext);
    const { product } = state;
    const { t, i18n } = useTranslation();
    const locale = i18n.language;

    const sortedDocRevisions = sortRevisionsByManualRevisionNumber(doc.revisions);
    const latestRevision = sortedDocRevisions[0] ?? null;
    const latestPublishedRevision = findLastValidPublishedRevision(sortedDocRevisions, product) ?? latestRevision;
    const latestRevisionDate = latestPublishedRevision.internalRevisionDate.toLocaleDateString(locale);

    return (
        <div
            ref={ref}
            className={`group flex w-full items-center text-gray-blue-700 max-w-[522px] gap-[16px] border bg-white p-4 transition-colors duration-200 ease-[ease] rounded-lg border-gray-200 hover:bg-gray-100`}>
            <DocumentCardThumbnail
                revision={latestPublishedRevision}
                doc={doc}
            />
            <div className="flex flex-col items-start w-full justify-center gap-3">
                {doc.categories &&
                    <div className="flex flex-wrap gap-2">
                        {doc.categories.slice(0, 2).map(e => (
                            <div key={e} className="px-[8px] py-[2px] text-[12px] rounded-[16px] bg-gray-blue-50 border border-gray-blue-200">
                                {t(`docs.categories.${e}`)}
                            </div>
                        ))}
                        {
                            doc.categories.length > 2 &&
                            <span className="py-[2px] px-[8px] rounded-full bg-gray-blue-50 text-[12px] border border-gray-blue-200 mix-blend-multiply">
                                +{doc.categories.length - 2}
                            </span>
                        }
                    </div>

                }

                <a
                    href={latestPublishedRevision.fileUrl}
                    target={"_blank"}
                    className="text-primary-600 font-semibold text-left line-clamp-2"
                    onClick={() => { window.dataLayer.push({ event: 'document_view_pdf', fileName: latestPublishedRevision.filename, documentId: doc._id }); }}
                >
                    {doc.documentName}
                </a>
                <div className="text-gray-700 text-[12px]">
                    {
                        doc.products_enriched?.length &&
                            doc.products_enriched?.length >= 3 ?
                            `${t("products.label")} ${doc.products_enriched?.length}`
                            :
                            doc.products_enriched?.map(pe => pe?.internalLabel)?.join(", ")
                    }
                </div>
                <div className="flex flex-col md:flex-row gap-3 justify-between md:items-center w-full">
                    <div className="flex flex-wrap gap-[8px] text-[12px] text-gray-400">
                        {doc.code && <span>{doc.code}</span>}
                        <span>{latestPublishedRevision.manualRevisionNumber || ""}</span>
                        {latestPublishedRevision && <span>{latestRevisionDate}</span>}
                        {doc.locale.length > 0 && <span>{[DOCUMENT_LOCALE[doc.locale[0] as keyof typeof DOCUMENT_LOCALE], doc.locale.length > 1 && "..."]}</span>}
                        {latestPublishedRevision?.filename && <span className="uppercase">{`.${getRevisionFileExtension(latestPublishedRevision.filename)}`}</span>}
                    </div>
                    <div className="flex items-center gap-[8px]">
                        {/* <button
                            className="h-[24px] flex items-center justify-center bg-white hover:bg-gray-100 border border-gray-300 shadow-[_0px_0.667px_1.333px_0px_rgba(16,24,40,0.05)] rounded-[6px] transition-background duration-200 px-[6px] gap-[6px] cursor-pointer"
                            onClick={() => {
                                modal?.handleModalOpen({
                                    title: t("revisions.title"),
                                    description: <RevisionHistory doc={doc} />
                                })
                            }}
                        >
                            <Icon icon="eye" />
                            <span className="text-xs font-semibold text-gray-700">{"Revisioni"}</span>
                        </button> */}
                        <DownloadButton
                            doc={doc}
                            revision={latestPublishedRevision}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
});